// src/components/family/EditChildModal.tsx
// Modal dialog for editing an existing child's profile

import { useEffect, useRef } from 'react';
import { X, Edit2, Loader2 } from 'lucide-react';
import { useChild, useUpdateChild } from '@/features/children/hooks/useChildren';
import { ChildForm } from './ChildForm';
import type { ChildFormData, Child } from '@/features/children/types/childTypes';

interface EditChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  childId: string;
  onSuccess?: () => void;
}

export function EditChildModal({ isOpen, onClose, childId, onSuccess }: EditChildModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { data: child, isLoading, isError, error } = useChild(childId);
  const updateChild = useUpdateChild(childId);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current && !isLoading) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();
      
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen, isLoading]);

  const handleSubmit = async (data: ChildFormData) => {
    try {
      await updateChild.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        nickname: data.nickname || undefined,
        date_of_birth: data.date_of_birth,
        gender: data.gender || undefined,
        birth_weight_oz: data.birth_weight_oz || undefined,
        birth_length_inches: data.birth_length_inches || undefined,
        gestational_age_weeks: data.gestational_age_weeks || undefined,
        birth_hospital: data.birth_hospital || undefined,
        birth_city: data.birth_city || undefined,
        birth_state: data.birth_state || undefined,
        delivery_type: data.delivery_type || undefined,
        blood_type: data.blood_type || undefined,
        allergies: data.allergies,
        insurance_provider: data.insurance_provider || undefined,
        insurance_policy_number: data.insurance_policy_number || undefined,
        medicaid_id: data.medicaid_id || undefined,
        notes: data.notes || undefined,
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by TanStack Query
      console.error('Failed to update child:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-child-title"
        className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lifted overflow-hidden animate-slideUp flex flex-col mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-gray/10 bg-gradient-to-r from-terracotta/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center">
              <Edit2 className="w-5 h-5 text-terracotta" />
            </div>
            <div>
              <h2 id="edit-child-title" className="font-display font-semibold text-lg text-navy">
                Edit Child Profile
              </h2>
              <p className="text-sm text-warm-gray">
                {child ? `Updating ${child.first_name}'s profile` : 'Loading...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-cream text-warm-gray hover:text-navy transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 text-sage animate-spin" />
              <p className="text-warm-gray">Loading child information...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="font-display font-semibold text-navy mb-2">
                Failed to load child
              </h3>
              <p className="text-sm text-warm-gray mb-4">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-cream hover:bg-cream-dark text-navy transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : child ? (
          <ChildForm
            initialData={child}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={updateChild.isPending}
            mode="edit"
          />
        ) : null}

        {/* Error Display */}
        {updateChild.isError && (
          <div className="absolute bottom-20 left-6 right-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700">
              {updateChild.error instanceof Error
                ? updateChild.error.message
                : 'Failed to update child. Please try again.'}
            </p>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
}

export default EditChildModal;

